import { type Ref, ref, computed } from "vue";
import apiSkobo from "@/helpers/api/apiSkobo";
import TrackHelper from "@/helpers/utilities/TrackHelper";
import { loadLanguageAsync } from "@/helpers/utilities/I18nHelper";
import { useFeaturesStore } from "@/stores/features";
import { useDocumentStore } from "@/stores/documents";
import { useMeStore } from "@/stores/me";
import { SessionHelper } from "@/helpers/utilities/sessionHelper";
import { setBrowserLocale } from "@/helpers/utilities/appGlobals";

// ---------- local types ----------
export interface ThemeSetting {
  typename?: string;
  key: string;
  value: string;
}

export interface ApplicationSetupApi {
  setTheme: (settings: ThemeSetting[]) => void;
}

interface SetupSetting {
  typename?: string;
  key: string;
  value: string;
}

interface SetupLocalization {
  key: string;
  value: string;
}

interface SetupFeature {
  key: string;
  enabled?: boolean;
  [key: string]: unknown;
}

interface SetupDriveDef {
  group: string;
  name: string;
  additionalData?: { drives?: Array<{ id: string; itemId: string }> };
}

interface SetupResponse {
  interfaces: { tenant: string; msGraphAPI: { graphAPIClientId: string } };
  localization: SetupLocalization[];
  settings: SetupSetting[];
  features: SetupFeature[];
  drives?: SetupDriveDef[];
  // The following are passed through to other stores; their precise shape lives there
  userProfile: unknown;
  sensitivityLabels: unknown;
  layout: ThemeSetting[];
  [key: string]: unknown;
}

interface ParsedApplicationSetup {
  interfaces?: { tenant: string; graphAPIClientId: string };
  language?: string;
  browserLocale?: string;
  localeFormats?: Record<string, unknown>;
  sharingListTypes?: { student: unknown[]; teacher: unknown[] };
  copyDocumentsToOneDrive?: boolean;
  copyDocumentsFromOnedrive?: boolean;
  copyDocumentsToSectionsStudent?: boolean;
  copyDocumentsFromSectionsStudent?: boolean;
  copyDocumentsToSectionsTeacher?: boolean;
  copyDocumentsFromSectionsTeacher?: boolean;
  showArchivedSections?: boolean;
  logEndpoints?: unknown[];
  os2faktorWaitForAuthenticationTimeout?: number;
  os2faktorSessionTimeout?: number;
  sectionConfiguration?: unknown;
  fileConfiguration?: Record<string, unknown>;
  navigationConfiguration?: unknown;
  shareToAulaDefaultDaysConfiguration?: unknown;
  confidentialNoteTypes?: unknown[];
  drives?: Array<{ group: string; name: string; driveId: string; itemId: string }>;
  [key: string]: unknown;
}

export const useApplicationSetupComposable = (api: ApplicationSetupApi) => {
  const applicationSetup: Ref<ParsedApplicationSetup> = ref({});
  const templateFoldersState: Ref<unknown> = ref(null);
  const templateStructureState: Ref<unknown> = ref(null);
  const templatespecialItemsState: Ref<unknown[] | null> = ref(null);

  const loadSetup = async (setupObject?: SetupResponse) => {
    try {
      if (!setupObject) {
        const result = await apiSkobo.get("/me/setup");
        setupObject = result.data;
      }

      setupApplicationTitle(setupObject);
      setupApplicationFavicon(setupObject);
      await setupLocalization(setupObject);
      const parsed = parseSetup(setupObject);
      applicationSetup.value = parsed;

      // Template setup
      const templateFoldersJson = setupObject!.settings.find(
        s => s.typename === "Templates" && s.key === "folders"
      )?.value;
      templateFoldersState.value = templateFoldersJson ? JSON.parse(templateFoldersJson) : null;

      const templateStructureJson = setupObject!.settings.find(
        s => s.typename === "Templates" && s.key === "structure"
      )?.value;
      templateStructureState.value = templateStructureJson ? JSON.parse(templateStructureJson) : null;

      const templateSpecialItemsJson = setupObject!.settings.find(
        s => s.typename === "Templates" && s.key === "specialitems"
      )?.value;
      templatespecialItemsState.value = templateSpecialItemsJson ? JSON.parse(templateSpecialItemsJson) : null;

      const featureStore = useFeaturesStore();
      featureStore.addFeatures(setupObject!.features);

      const documentStore = useDocumentStore();
      documentStore.setupSensitivityLabels(setupObject!.sensitivityLabels);

      const meStore = useMeStore();
      meStore.loadMe(setupObject!.userProfile);
      api.setTheme(setupObject!.layout);
    } catch (e) {
      TrackHelper.registerError(e);
      throw e;
    }
  };

  // Getters
  const templateDriveId = computed(() => {
    const templateDriveSetup = applicationSetup.value?.drives?.find(
      d => d.group === "Provided.O365.Teams.DocumentTemplate"
    );
    if (templateDriveSetup) {
      return templateDriveSetup.driveId;
    }
    return null;
  });
  const templateFolders = computed(() => templateFoldersState.value);
  const templateStructure = computed(() => templateStructureState.value);
  const templateSpecialItems = computed(() => templatespecialItemsState.value || []);

  const sectionConfiguration = computed(() => applicationSetup.value.sectionConfiguration);
  const navigationConfiguration = computed(() => applicationSetup.value.navigationConfiguration);
  const confidentialNoteTypes = computed(() => applicationSetup.value.confidentialNoteTypes || []);
  const fileConfiguration = computed(() => applicationSetup.value.fileConfiguration || {});
  const interfaces = computed(() => applicationSetup.value.interfaces);
  const shareToAulaDefaultDays = computed(() => applicationSetup.value.shareToAulaDefaultDaysConfiguration);

  const language = computed(() => applicationSetup.value.language);
  const browserLocale = computed(() => applicationSetup.value.browserLocale);
  const localeFormats = computed(() => applicationSetup.value.localeFormats || {});
  const logEndpoints = computed(() => applicationSetup.value.logEndpoints || []);

  const copyDocumentsFromSectionsStudent = computed(() => applicationSetup.value.copyDocumentsFromSectionsStudent);
  const copyDocumentsToSectionsStudent = computed(() => applicationSetup.value.copyDocumentsToSectionsStudent);
  const copyDocumentsFromSectionsTeacher = computed(() => applicationSetup.value.copyDocumentsFromSectionsTeacher);
  const copyDocumentsToSectionsTeacher = computed(() => applicationSetup.value.copyDocumentsToSectionsTeacher);

  const copyFromShouldIncludeSections = computed(() => {
    const meStore = useMeStore();
    return (
      (applicationSetup.value.copyDocumentsFromSectionsTeacher && meStore.isTeacher) ||
      (applicationSetup.value.copyDocumentsFromSectionsStudent && meStore.isStudent)
    );
  });
  const copyDocumentsToOneDrive = computed(() => applicationSetup.value.copyDocumentsToOneDrive);
  const copyDocumentsFromOnedrive = computed(() => applicationSetup.value.copyDocumentsFromOnedrive);

  const sharingListTypes = computed(() => applicationSetup.value.sharingListTypes || { student: [], teacher: [] });
  const showArchivedSections = computed(() => applicationSetup.value.showArchivedSections);
  const os2faktorSessionTimeout = computed(() => applicationSetup.value.os2faktorSessionTimeout);
  const os2faktorWaitForAuthenticationTimeout = computed(
    () => applicationSetup.value.os2faktorWaitForAuthenticationTimeout
  );

  const setting = computed(
    () => (settingName: string) => applicationSetup.value[settingName as keyof ParsedApplicationSetup]
  );

  return {
    applicationSetup,
    templateFoldersState,
    templateStructureState,
    templatespecialItemsState,
    loadSetup,
    templateDriveId,
    templateFolders,
    templateStructure,
    templateSpecialItems,
    sectionConfiguration,
    navigationConfiguration,
    confidentialNoteTypes,
    fileConfiguration,
    interfaces,
    shareToAulaDefaultDays,
    language,
    browserLocale,
    localeFormats,
    logEndpoints,
    copyDocumentsFromSectionsStudent,
    copyDocumentsToSectionsStudent,
    copyDocumentsFromSectionsTeacher,
    copyDocumentsToSectionsTeacher,
    copyFromShouldIncludeSections,
    copyDocumentsToOneDrive,
    copyDocumentsFromOnedrive,
    sharingListTypes,
    showArchivedSections,
    os2faktorSessionTimeout,
    os2faktorWaitForAuthenticationTimeout,
    setting
  };
};

// ---------- module-level helpers ----------

const parseSetup = (setup: SetupResponse): ParsedApplicationSetup => {
  const applicationSetup: ParsedApplicationSetup = {};

  applicationSetup.interfaces = {
    tenant: setup.interfaces.tenant,
    graphAPIClientId: setup.interfaces.msGraphAPI.graphAPIClientId
  };

  applicationSetup.browserLocale = setup.localization.find(k => k.key === "browserlocale")?.value;
  applicationSetup.language = setup.localization.find(k => k.key === "language")?.value;
  applicationSetup.localeFormats = {
    browserLocale: !setup || !applicationSetup.browserLocale ? "da-DK" : applicationSetup.browserLocale,
    shortDateFormatOptions: {
      year: "numeric",
      month: "numeric",
      day: "numeric"
    },
    longDateFormOptions: {
      year: "numeric",
      month: "long",
      day: "numeric"
    },
    shortDateFormOptions: {
      year: "numeric",
      month: "short",
      day: "numeric"
    },
    veryLongDateFormOptions: {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric"
    },
    timeOptions: {
      hour: "numeric",
      minute: "numeric"
    }
  };

  // Sharing listtypes setup
  const sharingListTypesSetup = setup.settings.find(s => s.key === "sharinglisttypesetup");

  applicationSetup.sharingListTypes = sharingListTypesSetup
    ? JSON.parse(sharingListTypesSetup.value)
    : { student: [], teacher: [] };

  // CopyTo and CopyFrom setups
  const copyDocumentsToOneDrive = setup.settings.find(s => s.key === "copydocumentstoonedrive");
  if (copyDocumentsToOneDrive) {
    applicationSetup.copyDocumentsToOneDrive = copyDocumentsToOneDrive.value === "true";
  }

  const copyDocumentsFromOnedrive = setup.settings.find(s => s.key === "copydocumentsfromdrive");
  if (copyDocumentsFromOnedrive) {
    applicationSetup.copyDocumentsFromOnedrive = copyDocumentsFromOnedrive.value === "true";
  }

  const copyDocumentsToSectionsStudent = setup.settings.find(s => s.key === "copydocumentstosectionsstudent");
  if (copyDocumentsToSectionsStudent) {
    applicationSetup.copyDocumentsToSectionsStudent = copyDocumentsToSectionsStudent.value === "true";
  }

  const copyDocumentsFromSectionsStudent = setup.settings.find(s => s.key === "copydocumentsfromsectionsstudent");
  if (copyDocumentsFromSectionsStudent) {
    applicationSetup.copyDocumentsFromSectionsStudent = copyDocumentsFromSectionsStudent.value === "true";
  }

  const copyDocumentsToSectionsTeacher = setup.settings.find(s => s.key === "copydocumentstosectionsteacher");
  if (copyDocumentsToSectionsTeacher) {
    applicationSetup.copyDocumentsToSectionsTeacher = copyDocumentsToSectionsTeacher.value === "true";
  }

  const copyDocumentsFromSectionsTeacher = setup.settings.find(s => s.key === "copydocumentsfromsectionsteacher");
  if (copyDocumentsFromSectionsTeacher) {
    applicationSetup.copyDocumentsFromSectionsTeacher = copyDocumentsFromSectionsTeacher.value === "true";
  }

  const showArchivedSectionsSetup = setup.settings.find(s => s.key === "showarchivedsections");
  if (showArchivedSectionsSetup) {
    applicationSetup.showArchivedSections = showArchivedSectionsSetup.value === "true";
  }

  const logEndpointsSetup = setup.settings.find(s => s.key === "logendpoints");
  if (logEndpointsSetup) {
    applicationSetup.logEndpoints = JSON.parse(logEndpointsSetup.value);
  }

  const os2faktorWaitForAuthenticationTimeout = setup.settings.find(
    s => s.key === "os2faktorwaitforauthenticationtimeout"
  );
  if (os2faktorWaitForAuthenticationTimeout) {
    applicationSetup.os2faktorWaitForAuthenticationTimeout = parseInt(os2faktorWaitForAuthenticationTimeout.value);
  }

  const os2faktorSessionTimeout = setup.settings.find(s => s.key === "os2faktorsessiontimeout");
  if (os2faktorSessionTimeout) {
    applicationSetup.os2faktorSessionTimeout = parseInt(os2faktorSessionTimeout.value);
  }

  const sectionConfiguration = setup.settings.find(s => s.key === "sectionconfiguration");
  if (sectionConfiguration) {
    applicationSetup.sectionConfiguration = JSON.parse(sectionConfiguration.value);
  }

  const fileConfiguration = setup.settings.find(s => s.key === "fileconfiguration");
  if (fileConfiguration) {
    applicationSetup.fileConfiguration = JSON.parse(fileConfiguration.value);
  }

  const navigationConfiguration = setup.settings.find(s => s.key === "navigationconfiguration");
  if (navigationConfiguration) {
    applicationSetup.navigationConfiguration = JSON.parse(navigationConfiguration.value);
  }

  const shareToAulaDefaultDaysConfiguration = setup.settings.find(s => s.key === "sharetoauladefaultdays");
  if (shareToAulaDefaultDaysConfiguration) {
    applicationSetup.shareToAulaDefaultDaysConfiguration = JSON.parse(shareToAulaDefaultDaysConfiguration.value);
  }

  const confidentialNoteTypes = setup.settings.find(s => s.key === "confidentialnotetypes");
  if (confidentialNoteTypes) {
    applicationSetup.confidentialNoteTypes = JSON.parse(confidentialNoteTypes.value);
  }

  // CleanUpDrives
  if (setup.drives) {
    applicationSetup.drives = setup.drives
      .filter(d => d.additionalData && d.additionalData.drives && d.additionalData.drives.length > 0)
      .map(d => ({
        group: d.group,
        name: d.name,
        driveId: d.additionalData!.drives![0].id,
        itemId: d.additionalData!.drives![0].itemId
      }));
  }

  return applicationSetup;
};

/**
 * @param { object } setup Setup object from api
 */
function setupApplicationFavicon(setup: SetupResponse) {
  const faviconSetup = setup.settings.find(k => k.key === "iconlocationuri");
  try {
    if (faviconSetup) {
      const linksInHead = Array.from(document.getElementsByTagName("link"));

      for (const link of linksInHead) {
        if (link.rel.indexOf("icon") > -1 || link.rel.indexOf("manifest") > -1) {
          if (!link.dataset.faviconPath) {
            const stripped = link.href.replace(/(http|https):\/\/.*\/assets\/icons/, "");
            if (stripped !== link.href) {
              link.dataset.faviconPath = stripped;
            }
          }
          if (link.dataset.faviconPath) {
            link.href = faviconSetup.value + link.dataset.faviconPath;
          }
        }
      }
    }
    // eslint-disable-next-line no-empty
  } catch {}
}

/**
 * @param { object } setup Setup object from api
 */
async function setupLocalization(setup: SetupResponse) {
  let languageSetup = setup.localization.find(k => k.key === "language");

  // Temporarily move to new language setup. Above line should be removed over time
  const languageSetupItem = setup.localization.find(k => k.key === "languagedefault");
  if (languageSetupItem) {
    languageSetup = languageSetupItem;
  }

  const languageVariantSetup = setup.localization.find(k => k.key === "languagevariant");
  const customerFileSetup = setup.localization.find(k => k.key === "customerfile");

  await loadLanguageAsync(
    !languageSetup ? "da" : languageSetup.value,
    !languageVariantSetup ? "skole" : languageVariantSetup.value,
    !customerFileSetup ? null : customerFileSetup.value
  );

  const browserLocale = setup.localization.find(k => k.key === "browserlocale");
  setBrowserLocale(!browserLocale ? "da-DK" : browserLocale.value);
}

/**
 * Sets the title bar of the browser
 * @param { object } setup Setup object from api
 */
function setupApplicationTitle(setup: SetupResponse) {
  const titleSetup = setup.settings.find(k => k.key === "browsertitle");
  if (titleSetup) {
    document.title = import.meta.env.VITE_APP_ENVIRONMENT_TITLE
      ? "(" + import.meta.env.VITE_APP_ENVIRONMENT_TITLE + ") "
      : "" + titleSetup.value;

    SessionHelper.addCustomerSystemName(titleSetup.value);
  }

  if (import.meta.env.VITE_APP_ENVIRONMENT_TITLE) {
    document.title = "(" + import.meta.env.VITE_APP_ENVIRONMENT_TITLE + ") " + document.title;
  }
}
